import { EventPageVisibility, AccommodationPlaceRoomBlockStatus, AccommodationPlaceType, EventRSVPAttendanceStatus } from '@graphql/generated-base';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useWebsitePages } from '@shared/hooks/useWebsitePages/useWebsitePages';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { useGetEventCTAButtonsDataQuery } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.graphql.generated';
import { useWeddingCodeBannerBookableHotelQuery } from './WeddingCodeBannerBookableHotel.graphql.generated';

export type WeddingCodeBannerEligibilityArgs = Readonly<{
  eventId: string;
  eventHandle: string;
  /** `event.info.weddingCode` — the per-event code string (null until the event is published). */
  weddingCode?: string | null;
}>;

export type WeddingCodeBannerEligibility = Readonly<{
  isEligible: boolean;
  /** Guest-site path to the accommodations page the banner links to. */
  accommodationsPath: string;
}>;

/**
 * Per-guest eligibility for the wedding-code banner. The gate (from the PRD clarifications) is:
 *   1. a code exists (`weddingCode`, set on publish),
 *   2. the accommodations page is public,
 *   3. host hasn't turned the banner off (`travelSettings.showWeddingCodeBanner`),
 *   4. the viewing guest has RSVP'd **attending**, and
 *   5. the event has ≥1 non-cancelled hotel room block.
 *
 * The banner-owned minimal query (`WeddingCodeBannerBookableHotel`) returns BOTH the host toggle
 * (`travelSettings.showWeddingCodeBanner`) and the fields needed for `hasBookableHotel` in one travel
 * read — so the toggle can't gate its own query; the local checks (code present + accommodations public)
 * gate it instead. The RSVP check reuses `GetEventCTAButtonsData` (shared with the ActionBar).
 */
export const useWeddingCodeBannerEligibility = ({ eventId, eventHandle, weddingCode }: WeddingCodeBannerEligibilityArgs): WeddingCodeBannerEligibility => {
  const { getPageByName } = useWebsitePages();
  const accommodationsPage = getPageByName(EVENT_PAGE_SLUGS.Accommodations);
  const accommodationsPath = `/${eventHandle}/${accommodationsPage?.pageSlug ?? EVENT_PAGE_SLUGS.Accommodations}`;

  // Experiment gate: banner + checkout weddingCode injection show only for the 'treatment' variant
  // ('control' is the holdout; default 'control' so FE ships dark). Gating here covers both the
  // banner and the checkout injection (Accommodations reads `isEligible`), keeping them in lockstep.
  const isInTreatment = useFeatureValue('weddingCodeBannerExperiment').value === 'treatment';

  // Publish gate: only show once the event is PUBLISHED. Publish is when registerWeddingGroupCode runs
  // and the code actually exists in discount_coupon_service. Pre-publish, `weddingCode` is derived from
  // the couple's names but is NOT registered — so showing it (e.g. in the owner's draft preview) would
  // advertise a code that can't be used yet. Reuses the guest site's own draft-vs-published signal.
  const isEventPublished = useWebsitePublishCapabilityFeature().eventPublishedState.value;

  // Local gates — no network. The host toggle comes from the query below (can't gate its own fetch).
  const hasCode = Boolean(weddingCode);
  const accommodationsPublic = accommodationsPage?.visibility === EventPageVisibility.public;
  const localGate = hasCode && accommodationsPublic;

  // Per-guest RSVP gate: only guests who RSVP'd **attending** see the banner. Reuses
  // GetEventCTAButtonsData (shared with the ActionBar), keyed to the viewing guest's activeSession.
  const { data: ctaData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventHandle },
    skip: !(isInTreatment && isEventPublished && localGate),
    fetchPolicy: 'network-only',
    batchMode: 'fast',
    // Keep this RSVP read off the SSR path (mirrors CtaButtons.controller): the server render can
    // cache the anonymous `activeSession`, and with cache-first that stale result would leave
    // `isAttending` false for an identified attending guest — hiding the banner. Fetch client-side.
    ssr: false,
  });
  const isAttending = ctaData?.eventByName?.activeSession?.eventCTA?.rsvpAttendanceStatus === EventRSVPAttendanceStatus.attending;

  // ≥1 bookable hotel — fetched once the local gate passes. This uses a banner-owned minimal query
  // (only the fields `hasBookableHotel` needs) rather than the accommodations widget's full query, so
  // the banner doesn't over-fetch on every guest page or reach into another widget's internals. On the
  // accommodations page these fields are already in Apollo's normalized cache, so cache-first is free.
  const { data } = useWeddingCodeBannerBookableHotelQuery({
    variables: { eventId },
    // Skip the travel read unless the guest is in-treatment, the event is published, the local gate
    // passes, and the guest is attending — i.e. only when the banner could actually show.
    skip: !(isInTreatment && isEventPublished && localGate && isAttending),
    fetchPolicy: 'cache-first',
    batchMode: 'fast',
    // Banner is deferred content, not needed for first paint — keep it off the guest SSR path
    // (narrow-SSR-surface convention; see layout-engine/README.md).
    ssr: false,
  });

  // Host opt-out toggle, from travel settings. Treat missing/null as OFF (require an explicit `true`):
  // travel_settings rows are sparse, so an event without a row — or a pre-feature NULL — must NOT show
  // the banner. New events default to `true` (DB default) once a travel_settings row exists.
  const bannerEnabled = data?.event?.travelSettings?.showWeddingCodeBanner === true;

  // "Bookable hotel through Joy (including availability)" — matches the guest "book on Joy" rule
  // (useAccommodationsCta): a catalog hotel that isn't just a custom link is bookable live through
  // Joy's booking engine, OR a Joy room block that still has availability (not cancelled).
  const hasBookableHotel = Boolean(
    data?.event?.accommodationPlacesV2?.some(place => {
      const isCatalogBookableHotel = place.type === AccommodationPlaceType.hotel && !place.isCustomLinkEnabled && place.isFromAccommodationCatalog;
      const hasActiveRoomBlock = Boolean(place.accommodationRoomBlock) && place.accommodationRoomBlock?.status !== AccommodationPlaceRoomBlockStatus.cancelled;
      return isCatalogBookableHotel || hasActiveRoomBlock;
    }),
  );

  return {
    // Eligible only when: in the experiment treatment, event published, code present + accommodations
    // public (localGate), guest RSVP'd attending, host toggle on, and there's ≥1 Joy-bookable hotel.
    isEligible: isInTreatment && isEventPublished && localGate && isAttending && bannerEnabled && hasBookableHotel,
    accommodationsPath,
  };
};
